import { Emitter } from "../../../../base/common/events";
import { IS_EMBEDDED_INSPECTOR } from "../../../../base/common/needle.js";
import { IStartup } from "../../../../base/common/lifecycle";
import { ILogService, LogLevel } from "../../../../platform/log/common/log";
import { IPropertyWithType, PropertyMetadata } from "../../../../platform/properties/common/properties";
import { ISecretStorageService } from "../../../../platform/secrets/common/secrets";
import { IStorageService, StorageScope, StorageTarget } from "../../../../platform/storage/common/storage";
import { configAsProperty, type CameraFlyBackground, IUserConfiguration, UserConfigurationChangeEvent } from "./userConfiguration";
import { BUILTIN_WORKSPACES, POPOUT_WORKSPACES, popoutWorkspaceDefinition, validateWorkspaceDefinition, type WorkspaceDefinition, type WorkspaceSurface } from "./workspaceDefinitions";

export class UserConfiguration implements IUserConfiguration, IStartup {

    private readonly additionalWorkspaces = new Map<string, WorkspaceDefinition>();
    private workspaceCatalog: readonly WorkspaceDefinition[] | undefined;

    get workspaceCatalogIsFixed(): boolean { return this.workspaceCatalog !== undefined; }

    setWorkspaceCatalog(definitions: readonly WorkspaceDefinition[]): void {
        if (!definitions.length) throw new Error("Workspace catalog needs at least one workspace");
        const ids = new Set<string>();
        for (const definition of definitions) {
            const issues = validateWorkspaceDefinition(definition);
            if (issues.length) throw new Error(`Invalid workspace "${definition.id}": ${issues.join("; ")}`);
            if (ids.has(definition.id)) throw new Error(`Duplicate workspace "${definition.id}"`);
            ids.add(definition.id);
        }
        this.workspaceCatalog = [...definitions];
        this.notifyChanged();
    }

    registerWorkspaceDefinition(definition: WorkspaceDefinition): void {
        const issues = validateWorkspaceDefinition(definition);
        if (issues.length) throw new Error(`Invalid workspace "${definition.id}": ${issues.join("; ")}`);
        if (BUILTIN_WORKSPACES.some(existing => existing.id === definition.id) || this.additionalWorkspaces.has(definition.id)) {
            throw new Error(`Workspace "${definition.id}" is already registered`);
        }
        this.additionalWorkspaces.set(definition.id, definition);
    }

    getWorkspaceDefinitions(surface: WorkspaceSurface = 'embedded'): readonly WorkspaceDefinition[] {
        if (this.workspaceCatalog) return surface === 'popout'
            ? this.workspaceCatalog.map(popoutWorkspaceDefinition)
            : this.workspaceCatalog;
        const additional = [...this.additionalWorkspaces.values()];
        return surface === 'popout'
            ? [...POPOUT_WORKSPACES, ...additional.map(popoutWorkspaceDefinition)]
            : [...BUILTIN_WORKSPACES, ...additional];
    }

    getWorkspaceDefinition(id: string, surface: WorkspaceSurface = 'embedded'): WorkspaceDefinition | undefined {
        return this.getWorkspaceDefinitions(surface).find(definition => definition.id === id);
    }

    getWorkspaceDisplayName(id: string): string {
        return this.getWorkspaceDefinition(id)?.name ?? id;
    }

    private readonly _onDidChange = new Emitter<UserConfigurationChangeEvent>();
    readonly onDidChange = this._onDidChange.event;
    private notifyChanged() {
        this._onDidChange.fire({});
    }

    private readonly _properties = new Map<string, IPropertyWithType<any>>();
    getProperty<T extends keyof IUserConfiguration>(key: T, meta?: PropertyMetadata): IPropertyWithType<IUserConfiguration[T]> {
        if (this._properties.has(key as string)) {
            return this._properties.get(key as string) as IPropertyWithType<IUserConfiguration[T]>;
        }
        const prop = configAsProperty(this, key, meta);
        this._properties.set(key as string, prop);
        return prop;
    }


    constructor(
        @ILogService private readonly logger: ILogService,
        @IStorageService private readonly storageService: IStorageService,
        @ISecretStorageService private readonly secretStorageService: ISecretStorageService,
    ) {

        const storedLogLevel = this.storageService.get("log.level", StorageScope.PROFILE);
        if (storedLogLevel) {
            const level = LogLevel[storedLogLevel as keyof typeof LogLevel];
            this.logger.setLevel(level);
        }
    }



    initialize(): Promise<void> {
        // BEFORE any UI reads the workspace list. WorkspaceTabs mounts ahead of
        // the dock (editor.svelte), so seeding from the dock's setup left a
        // freshly updated user looking at a lone "Default" tab.
        this.seedWorkspaces();
        return Promise.resolve();
    }


    // #region log level
    canChangeLogLevel: boolean = false;
    get logLevel(): LogLevel {
        const level = this.logger.getLevel();
        return level;
    }
    set logLevel(level: LogLevel) {
        const current = this.logger.getLevel();
        if (current === level) return;
        this.storageService.store("log.level", LogLevel[level], StorageScope.PROFILE, StorageTarget.USER);
        this.logger.setLevel(level);
        this.notifyChanged();
    }
    get availableLogLevels(): Readonly<Record<LogLevel, string>> {
        return logLevelOptions;
    }



    // #region theme
    get theme(): string {
        return this.storageService.get("ui.theme", StorageScope.APPLICATION) || "default";
    }
    set theme(value: string) {
        if (value === this.theme) return;
        this.storageService.store("ui.theme", value, StorageScope.APPLICATION, StorageTarget.USER);
        this.notifyChanged();
    }

    get hierarchyFilterInactive(): boolean {
        return this.storageService.getBoolean("hierarchy.filterMode.inactive", StorageScope.APPLICATION, false) ?? false;
    }
    set hierarchyFilterInactive(value: boolean) {
        if (value === this.hierarchyFilterInactive) return;
        this.storageService.store("hierarchy.filterMode.inactive", value, StorageScope.APPLICATION, StorageTarget.USER);
        this.notifyChanged();
    }

    get richTooltip() {
        return this.storageService.getBoolean("ui.rich-tooltip", StorageScope.APPLICATION, true) ?? true;
    }
    set richTooltip(value: boolean) {
        if (value === this.richTooltip) return;
        this.storageService.store("ui.rich-tooltip", value, StorageScope.APPLICATION, StorageTarget.USER);
        this.notifyChanged();
    }

    get aiRevisionDescriptions() {
        return this.storageService.getBoolean("ai.revision-descriptions", StorageScope.APPLICATION, true) ?? true;
    }
    set aiRevisionDescriptions(value: boolean) {
        if (value === this.aiRevisionDescriptions) return;
        this.storageService.store("ai.revision-descriptions", value, StorageScope.APPLICATION, StorageTarget.USER);
        this.notifyChanged();
    }


    // #region ui layout
    get uiLayout(): "default" | "minimal" {
        if (!IS_EMBEDDED_INSPECTOR) return "default";
        return this.storageService.get("ui.layout-style", StorageScope.APPLICATION) as "default" | "minimal" || "default";
    }
    set uiLayout(value: "default" | "minimal") {
        if (!IS_EMBEDDED_INSPECTOR) return;
        if (value === this.uiLayout) return;
        this.storageService.store("ui.layout-style", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    // #region dock workspace layouts (dockview toJSON payloads, per name)
    // PROFILE scope: in the chrome extension this is backed by
    // chrome.storage.sync, so workspaces roam with the browser profile.
    private static readonly DEFAULT_WORKSPACE = "Default";
    /**
     * Named layouts live at `ui.workspace.dock-layout.<name>`; the bare key
     * (no name suffix) is the LEGACY pre-named-workspaces slot (single
     * layout, APPLICATION scope) which is read as the "Default" workspace.
     */
    private static readonly WORKSPACE_LAYOUT_KEY = "ui.workspace.dock-layout";
    private static readonly WORKSPACE_INDEX_KEY = "ui.workspace.layouts";

    private _workspaceSurface: WorkspaceSurface = 'embedded';
    get workspaceSurface(): WorkspaceSurface { return this._workspaceSurface; }
    set workspaceSurface(surface: WorkspaceSurface) {
        if (surface === this._workspaceSurface) return;
        this._workspaceSurface = surface;
        this.notifyChanged();
    }

    getActiveWorkspace(surface: WorkspaceSurface): string {
        const stored = this.storageService.get(this.workspaceKey("ui.workspace.active", surface), StorageScope.PROFILE);
        if (this.workspaceCatalog) return this.workspaceCatalog.some(definition => definition.id === stored)
            ? stored! : this.workspaceCatalog[0].id;
        return stored || UserConfiguration.DEFAULT_WORKSPACE;
    }
    get activeWorkspace(): string { return this.getActiveWorkspace(this.workspaceSurface); }
    set activeWorkspace(value: string) {
        this.setActiveWorkspace(value, this.workspaceSurface);
    }
    private setActiveWorkspace(value: string, surface: WorkspaceSurface): void {
        const name = value?.trim();
        if (this.workspaceCatalog && !this.workspaceCatalog.some(definition => definition.id === name)) return;
        if (!name || name === this.getActiveWorkspace(surface)) return;
        this.storageService.store(this.workspaceKey("ui.workspace.active", surface), name, StorageScope.PROFILE, StorageTarget.USER);
        this.notifyChanged();
    }

    /** The raw stored workspace-name index (names with a persisted layout). */
    private readWorkspaceIndex(): string[] {
        const raw = this.storageService.get(UserConfiguration.WORKSPACE_INDEX_KEY, StorageScope.PROFILE);
        if (raw) {
            try {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    return parsed.filter((entry): entry is string => typeof entry === "string" && entry.length > 0);
                }
            }
            catch {
                // corrupt index — fall through to empty
            }
        }
        return [];
    }
    private storeWorkspaceIndex(names: string[]): void {
        this.storageService.store(UserConfiguration.WORKSPACE_INDEX_KEY, JSON.stringify(names), StorageScope.PROFILE, StorageTarget.USER);
    }

    listWorkspaceLayouts(): string[] {
        if (this.workspaceCatalog) return this.workspaceCatalog.map(definition => definition.id);
        const names = this.readWorkspaceIndex();
        if (names.length === 0) names.push(UserConfiguration.DEFAULT_WORKSPACE);
        // The active workspace may not have a persisted layout yet (freshly
        // created) — it must still be listed.
        for (const surface of ['embedded', 'popout'] as const) {
            const active = this.getActiveWorkspace(surface);
            if (!names.includes(active)) names.push(active);
        }
        return names;
    }

    private workspaceKey(base: string, surface: WorkspaceSurface): string {
        return surface === 'popout' ? `popout.${base}` : base;
    }

    getWorkspaceLayout(name: string, surface: WorkspaceSurface = 'embedded'): string | undefined {
        // Single source of truth. The legacy un-keyed slot is MOVED here by
        // migrateLegacyLayout() rather than read as a fallback: a fallback read
        // silently undid every version bump on "Default", because the bump
        // removes this PROFILE key and the read then found the legacy blob again.
        return this.storageService.get(`${this.workspaceKey(UserConfiguration.WORKSPACE_LAYOUT_KEY, surface)}.${name}`, StorageScope.PROFILE);
    }
    setWorkspaceLayout(name: string, layout: string | undefined, surface: WorkspaceSurface = 'embedded'): void {
        const key = `${this.workspaceKey(UserConfiguration.WORKSPACE_LAYOUT_KEY, surface)}.${name}`;
        const index = this.readWorkspaceIndex();
        if (layout === undefined) {
            this.storageService.remove(key, StorageScope.PROFILE);
            const existing = index.indexOf(name);
            if (existing !== -1 && this.getWorkspaceLayout(name, surface === 'embedded' ? 'popout' : 'embedded') === undefined) {
                index.splice(existing, 1);
                this.storeWorkspaceIndex(index);
            }
            return;
        }
        // Deliberately no notifyChanged(): the layout blob is read once on
        // dock init/switch, not reactive UI state — firing the global config
        // event on every (debounced) drag would re-render config subscribers.
        this.storageService.store(key, layout, StorageScope.PROFILE, StorageTarget.USER);
        if (!index.includes(name)) {
            index.push(name);
            this.storeWorkspaceIndex(index);
        }
    }

    /** built-in recipe versions as last seeded — {name: version} */
    private static readonly WORKSPACE_VERSIONS_KEY = "ui.workspace.builtinVersions";
    /** recipe panels already injected into a workspace, ever (workspaceReconcile) */
    private static readonly OFFERED_PANELS_KEY = "ui.workspace.offeredPanels";

    getOfferedPanels(workspace: string, surface: WorkspaceSurface = 'embedded'): string[] {
        const raw = this.storageService.get(`${this.workspaceKey(UserConfiguration.OFFERED_PANELS_KEY, surface)}.${workspace}`, StorageScope.PROFILE);
        return raw ? raw.split(",").filter(Boolean) : [];
    }
    setOfferedPanels(workspace: string, panelIds: readonly string[], surface: WorkspaceSurface = 'embedded'): void {
        // comma-joined, like the dock's openedNewPanels: PROFILE scope is
        // chrome.storage.sync in the extension, whose per-item quota is small
        this.storageService.store(`${this.workspaceKey(UserConfiguration.OFFERED_PANELS_KEY, surface)}.${workspace}`,
            [...new Set(panelIds)].join(","), StorageScope.PROFILE, StorageTarget.USER);
    }

    /**
     * MIGRATION (one-time): the dock layout used to live in a single un-keyed
     * slot at APPLICATION scope, before workspaces were named. MOVE it into the
     * "Default" workspace's key and clear the old slot, so there is exactly one
     * place a layout can come from.
     */
    private migrateLegacyLayout(): void {
        const legacy = this.storageService.get(UserConfiguration.WORKSPACE_LAYOUT_KEY, StorageScope.APPLICATION);
        if (legacy === undefined) return;
        // a current layout always wins; the stale slot goes either way
        if (this.getWorkspaceLayout(UserConfiguration.DEFAULT_WORKSPACE) === undefined) {
            this.setWorkspaceLayout(UserConfiguration.DEFAULT_WORKSPACE, legacy);
        }
        this.storageService.remove(UserConfiguration.WORKSPACE_LAYOUT_KEY, StorageScope.APPLICATION);
    }

    seedWorkspaces(surface: WorkspaceSurface = 'embedded'): void {
        if (surface === 'embedded') this.migrateLegacyLayout();
        // ensure-present: built-ins are PROTECTED (undeletable, see the
        // guards below), so reseeding on every boot is harmless self-healing
        const index = this.readWorkspaceIndex();
        let indexChanged = false;
        let seenVersions: Record<string, number> = {};
        const versionsKey = this.workspaceKey(UserConfiguration.WORKSPACE_VERSIONS_KEY, surface);
        const rawVersions = this.storageService.get(versionsKey, StorageScope.PROFILE);
        if (rawVersions) {
            try {
                const parsed = JSON.parse(rawVersions);
                if (parsed && typeof parsed === "object") seenVersions = parsed as Record<string, number>;
            }
            catch { /* corrupt — treat as first seed (records, never wipes) */ }
        }
        let versionsChanged = false;
        for (const definition of this.getWorkspaceDefinitions(surface)) {
            const name = definition.id; // the STABLE storage key, not the display name
            if (!index.includes(name)) {
                index.push(name);
                indexChanged = true;
            }
            const seen = seenVersions[name];
            if (seen === definition.version) continue;
            // a NEWER shipped recipe RESTORES the workspace: drop the user's
            // saved arrangement (it shadows the recipe — unmodified built-ins
            // have no blob and update without this). The very first seed only
            // records versions, so pre-version blobs survive the migration.
            if (seen !== undefined) {
                this.storageService.remove(`${this.workspaceKey(UserConfiguration.WORKSPACE_LAYOUT_KEY, surface)}.${name}`, StorageScope.PROFILE);
                // the offered set describes a blob that no longer exists — the
                // rebuilt recipe brings its panels back and owes them afresh
                this.storageService.remove(`${this.workspaceKey(UserConfiguration.OFFERED_PANELS_KEY, surface)}.${name}`, StorageScope.PROFILE);
            }
            seenVersions[name] = definition.version;
            versionsChanged = true;
        }
        if (indexChanged) {
            this.storeWorkspaceIndex(index);
            // a tab strip that already mounted computed its list pre-seed —
            // tell it to re-read, or the new built-ins stay invisible
            this.notifyChanged();
        }
        if (versionsChanged) this.storageService.store(versionsKey, JSON.stringify(seenVersions), StorageScope.PROFILE, StorageTarget.USER);
    }

    /** Remove a name from the workspace index (if present). */
    private removeFromWorkspaceIndex(name: string): void {
        const index = this.readWorkspaceIndex();
        const existing = index.indexOf(name);
        if (existing !== -1) {
            index.splice(existing, 1);
            this.storeWorkspaceIndex(index);
        }
    }

    renameWorkspaceLayout(oldName: string, newName: string): void {
        if (this.workspaceCatalog) return;
        const from = oldName?.trim();
        const to = newName?.trim();
        if (!from || !to || from === to) return;
        // BUILT-IN workspaces (incl. "Default") can neither be renamed nor be
        // overwritten by a rename — they ship with the editor (Marcel
        // 2026-08-19; the picker hides the buttons, this guard is the law)
        if (this.getWorkspaceDefinition(from) || this.getWorkspaceDefinition(to)) return;

        const activeSurfaces = (['embedded', 'popout'] as const).filter(surface => this.getActiveWorkspace(surface) === from);
        const wasActive = activeSurfaces.length > 0;
        if (!wasActive && !this.readWorkspaceIndex().includes(from)
            && this.getWorkspaceLayout(from) === undefined
            && this.getWorkspaceLayout(from, 'popout') === undefined) return;

        const moveState = () => {
            for (const surface of ['embedded', 'popout'] as const) {
                for (const base of [UserConfiguration.WORKSPACE_LAYOUT_KEY, UserConfiguration.OFFERED_PANELS_KEY]) {
                    const oldKey = `${this.workspaceKey(base, surface)}.${from}`;
                    const newKey = `${this.workspaceKey(base, surface)}.${to}`;
                    const value = this.storageService.get(oldKey, StorageScope.PROFILE);
                    if (value !== undefined) this.storageService.store(newKey, value, StorageScope.PROFILE, StorageTarget.USER);
                    this.storageService.remove(oldKey, StorageScope.PROFILE);
                }
            }
        };
        moveState();

        // Update the index: replace the old name in place.
        const index = this.readWorkspaceIndex();
        const existing = index.indexOf(from);
        if (existing !== -1) {
            if (index.includes(to)) index.splice(existing, 1);
            else index[existing] = to;
            this.storeWorkspaceIndex(index);
        }

        if (wasActive) {
            // Fires onDidChange: the dock persists the OUTGOING (live)
            // layout under the OLD name first, then restores the layout we
            // just moved to the new name.
            for (const surface of activeSurfaces) this.setActiveWorkspace(to, surface);
            // That outgoing persist resurrected the old key/index entry —
            // and it is the freshest state, so keep it under the NEW name.
            moveState();
            this.removeFromWorkspaceIndex(from);
        }
        // The activeWorkspace setter above only fires for the ACTIVE
        // workspace — a rename of any OTHER workspace must notify too, or
        // list consumers (the tab strip) keep showing the old name.
        this.notifyChanged();
    }

    deleteWorkspaceLayout(name: string): void {
        if (this.workspaceCatalog) return;
        const workspace = name?.trim();
        // built-ins ship with the editor — never deletable (same rule as rename)
        if (!workspace || this.getWorkspaceDefinition(workspace)) return;

        const activeSurfaces = (['embedded', 'popout'] as const).filter(surface => this.getActiveWorkspace(surface) === workspace);
        const wasActive = activeSurfaces.length > 0;
        // deleting a workspace that does not exist stays silent
        if (!wasActive && !this.readWorkspaceIndex().includes(workspace)
            && this.getWorkspaceLayout(workspace) === undefined
            && this.getWorkspaceLayout(workspace, 'popout') === undefined) return;

        const removeState = () => {
            for (const surface of ['embedded', 'popout'] as const) {
                this.setWorkspaceLayout(workspace, undefined, surface);
                this.storageService.remove(`${this.workspaceKey(UserConfiguration.OFFERED_PANELS_KEY, surface)}.${workspace}`, StorageScope.PROFILE);
            }
        };
        removeState();
        if (wasActive) {
            for (const surface of activeSurfaces) this.setActiveWorkspace(UserConfiguration.DEFAULT_WORKSPACE, surface);
            // The dock's outgoing persist runs during the active-workspace change.
            removeState();
        }
        // same as rename: non-active deletes must notify the tab strip too
        this.notifyChanged();
    }

    // #region webgl scene picking
    get webglPicking(): boolean {
        // On by default — click-to-select is a core editor affordance. Users who
        // explicitly disabled it keep their stored `false`; only the unset default
        // flips (toggle it off via the toolbar).
        return this.storageService.getBoolean("webgl.picking", StorageScope.APPLICATION) ?? true;
    }
    set webglPicking(value: boolean) {
        if (value === this.webglPicking) return;
        this.storageService.store("webgl.picking", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    // #region selection display (frame + gizmo handles)
    get selectionDisplay(): boolean {
        // On by default — seeing what you selected is the baseline.
        return this.storageService.getBoolean("webgl.selection-display", StorageScope.APPLICATION) ?? true;
    }
    set selectionDisplay(value: boolean) {
        if (value === this.selectionDisplay) return;
        this.storageService.store("webgl.selection-display", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    // #region GPU timestamps (chrome extension)
    get gpuTimestamps(): boolean {
        // On by default: the measurement is the feature, and it costs nothing
        // until the user asks for a profile.
        return this.storageService.getBoolean("webgl.gpu-timestamps", StorageScope.APPLICATION) ?? true;
    }
    set gpuTimestamps(value: boolean) {
        if (value === this.gpuTimestamps) return;
        this.storageService.store("webgl.gpu-timestamps", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    // #region gizmo space + pivot
    get gizmoSpace(): "world" | "local" {
        return this.storageService.get("gizmo.space", StorageScope.APPLICATION) as "world" | "local" || "world";
    }
    set gizmoSpace(value: "world" | "local") {
        if (value === this.gizmoSpace) return;
        this.storageService.store("gizmo.space", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }
    get gizmoPivot(): "center" | "anchor" {
        return this.storageService.get("gizmo.pivot", StorageScope.APPLICATION) as "center" | "anchor" || "center";
    }
    set gizmoPivot(value: "center" | "anchor") {
        if (value === this.gizmoPivot) return;
        this.storageService.store("gizmo.pivot", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    get gizmoLinkLightTarget(): boolean {
        // default false — see the interface: three's own behaviour is a target
        // that stays put, and locking is the opt-in
        return this.storageService.getBoolean("gizmo.linkLightTarget", StorageScope.APPLICATION, false);
    }
    set gizmoLinkLightTarget(value: boolean) {
        if (value === this.gizmoLinkLightTarget) return;
        this.storageService.store("gizmo.linkLightTarget", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    // #region canvas shortcuts (chrome extension)
    get canvasShortcuts(): boolean {
        return this.storageService.getBoolean("keyboard.canvas-shortcuts", StorageScope.APPLICATION) ?? true;
    }
    set canvasShortcuts(value: boolean) {
        if (value === this.canvasShortcuts) return;
        this.storageService.store("keyboard.canvas-shortcuts", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    // #region camera controls
    get cameraKeyboardMouseEnabled(): boolean {
        return this.storageService.getBoolean("camera.keyboard-mouse.enabled", StorageScope.APPLICATION, true) ?? true;
    }
    set cameraKeyboardMouseEnabled(value: boolean) {
        if (value === this.cameraKeyboardMouseEnabled) return;
        this.storageService.store("camera.keyboard-mouse.enabled", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    get cameraKeyboardMouseCapture(): boolean {
        return this.storageService.getBoolean("camera.keyboard-mouse.capture", StorageScope.APPLICATION, true) ?? true;
    }
    set cameraKeyboardMouseCapture(value: boolean) {
        if (value === this.cameraKeyboardMouseCapture) return;
        this.storageService.store("camera.keyboard-mouse.capture", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    get cameraGamepadEnabled(): boolean {
        return this.storageService.getBoolean("camera.gamepad.enabled", StorageScope.APPLICATION, true) ?? true;
    }
    set cameraGamepadEnabled(value: boolean) {
        if (value === this.cameraGamepadEnabled) return;
        this.storageService.store("camera.gamepad.enabled", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    get cameraGamepadDeadzone(): number {
        const raw = this.storageService.get("camera.gamepad.deadzone", StorageScope.APPLICATION);
        if (raw === undefined) return .18;
        const value = parseFloat(raw);
        return isNaN(value) ? .18 : Math.max(0, Math.min(.95, value));
    }
    set cameraGamepadDeadzone(value: number) {
        const normalized = Math.max(0, Math.min(.95, value));
        if (normalized === this.cameraGamepadDeadzone) return;
        this.storageService.store("camera.gamepad.deadzone", normalized, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    get cameraSpaceMouseEnabled(): boolean {
        return this.storageService.getBoolean("camera.spacemouse.enabled", StorageScope.APPLICATION, true) ?? true;
    }
    set cameraSpaceMouseEnabled(value: boolean) {
        if (value === this.cameraSpaceMouseEnabled) return;
        this.storageService.store("camera.spacemouse.enabled", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    get cameraSmoothing(): number {
        const raw = this.storageService.get("camera.smoothing", StorageScope.APPLICATION);
        if (raw !== undefined) {
            return clampCameraSmoothing(raw);
        }

        const legacyTranslation = this.storageService.get("camera.translation.smoothing", StorageScope.APPLICATION);
        const legacyRotation = this.storageService.get("camera.rotation.smoothing", StorageScope.APPLICATION);
        if (legacyTranslation === undefined && legacyRotation === undefined) {
            return .5;
        }

        const legacyValues = [legacyTranslation, legacyRotation]
            .filter(value => value !== undefined)
            .map(value => clampCameraSmoothing(value));
        if (legacyValues.length === 0) {
            return .5;
        }
        const average = legacyValues.reduce((sum, value) => sum + value, 0) / legacyValues.length;
        return clampCameraSmoothing(average);
    }
    set cameraSmoothing(value: number) {
        const normalized = clampCameraSmoothing(value);
        if (normalized === this.cameraSmoothing) return;
        this.storageService.store("camera.smoothing", normalized, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.storageService.remove("camera.translation.smoothing", StorageScope.APPLICATION);
        this.storageService.remove("camera.rotation.smoothing", StorageScope.APPLICATION);
        this.notifyChanged();
    }

    get cameraSpeed(): number {
        const raw = this.storageService.get("camera.speed", StorageScope.APPLICATION);
        if (raw === undefined) return 1;
        return clampCameraSpeed(raw);
    }
    set cameraSpeed(value: number) {
        const normalized = clampCameraSpeed(value);
        if (normalized === this.cameraSpeed) return;
        this.storageService.store("camera.speed", normalized, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    get cameraRotationSpeedMultiplier(): number {
        const raw = this.storageService.get("camera.rotation-speed.multiplier", StorageScope.APPLICATION);
        if (raw === undefined) return 1;
        return clampCameraSpeed(raw);
    }
    set cameraRotationSpeedMultiplier(value: number) {
        const normalized = clampCameraSpeed(value);
        if (normalized === this.cameraRotationSpeedMultiplier) return;
        this.storageService.store("camera.rotation-speed.multiplier", normalized, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    get cameraUseRotationCenter(): boolean {
        return this.storageService.getBoolean("camera.rotation-center.enabled", StorageScope.APPLICATION, true) ?? true;
    }
    set cameraUseRotationCenter(value: boolean) {
        if (value === this.cameraUseRotationCenter) return;
        this.storageService.store("camera.rotation-center.enabled", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    get cameraFocusCanvasOnFly(): boolean {
        return this.storageService.getBoolean("camera.fly.focus-canvas", StorageScope.APPLICATION, true) ?? true;
    }
    set cameraFocusCanvasOnFly(value: boolean) {
        if (value === this.cameraFocusCanvasOnFly) return;
        this.storageService.store("camera.fly.focus-canvas", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    get cameraFillCanvasOnFly(): boolean {
        return this.storageService.getBoolean("camera.fly.fill-canvas", StorageScope.APPLICATION, false) ?? false;
    }
    set cameraFillCanvasOnFly(value: boolean) {
        if (value === this.cameraFillCanvasOnFly) return;
        this.storageService.store("camera.fly.fill-canvas", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    get cameraRenderThroughMainCamera(): boolean {
        return this.storageService.getBoolean("camera.fly.render-through-main-camera", StorageScope.APPLICATION, false) ?? false;
    }
    set cameraRenderThroughMainCamera(value: boolean) {
        if (value === this.cameraRenderThroughMainCamera) return;
        this.storageService.store("camera.fly.render-through-main-camera", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    get cameraFlyBackground(): CameraFlyBackground {
        const stored = this.storageService.get("camera.fly.background", StorageScope.APPLICATION);
        if (stored === "checker-dark") return "checker-dark";
        if (stored === "checker-light" || stored === "checkerboard") return "checker-light";
        return "auto";
    }
    set cameraFlyBackground(value: CameraFlyBackground) {
        const normalized: CameraFlyBackground = value === "checker-dark"
            ? "checker-dark"
            : value === "checker-light" ? "checker-light" : "auto";
        if (normalized === this.cameraFlyBackground) return;
        this.storageService.store("camera.fly.background", normalized, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }

    get cameraTranslationSmoothing(): number {
        return this.cameraSmoothing;
    }
    set cameraTranslationSmoothing(value: number) {
        this.cameraSmoothing = value;
    }

    get cameraRotationSmoothing(): number {
        return this.cameraSmoothing;
    }
    set cameraRotationSmoothing(value: number) {
        this.cameraSmoothing = value;
    }


    // #region property settings
    get propertyAutoRefresh(): boolean {
        return this.storageService.getBoolean("property.auto-refresh", StorageScope.APPLICATION, true) ?? true;
    }
    set propertyAutoRefresh(value: boolean) {
        if (value === this.propertyAutoRefresh) return;
        this.storageService.store("property.auto-refresh", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }


    // #region support button
    get showSupportButton(): boolean {
        return this.storageService.getBoolean("ui.show-support-button", StorageScope.APPLICATION, true) ?? true;
    }
    set showSupportButton(value: boolean) {
        if (value === this.showSupportButton) return;
        this.storageService.store("ui.show-support-button", value, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }



    // #region panel collapsed state
    setPanelVisible(panel: string, collapsed: boolean): void {
        const current = this.isPanelVisible(panel);
        if (current === collapsed) return;
        this.storageService.store(`ui.panel.${panel}.visible`, collapsed, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }
    isPanelVisible(panel: string, fallbackValue?: boolean): boolean {
        const val = this.storageService.getBoolean(`ui.panel.${panel}.visible`, StorageScope.APPLICATION);
        if (typeof val === "boolean") {
            return val;
        }
        return fallbackValue ?? true;
    }

    // #region panel collapsed state
    setPanelCollapsed(panel: string, collapsed: boolean): void {
        const current = this.isPanelCollapsed(panel);
        if (current === collapsed) return;
        this.storageService.store(`ui.panel.${panel}.collapsed`, collapsed, StorageScope.APPLICATION, StorageTarget.MACHINE);
        this.notifyChanged();
    }
    isPanelCollapsed(panel: string, fallbackValue?: boolean): boolean {
        const val = this.storageService.getBoolean(`ui.panel.${panel}.collapsed`, StorageScope.APPLICATION);
        if (typeof val === "boolean") {
            return val;
        }
        return fallbackValue ?? false;
    }
}


const logLevelOptions: Readonly<Record<LogLevel, string>> = {
    [LogLevel.Off]: "OFF",
    [LogLevel.Trace]: "TRACE",
    [LogLevel.Debug]: "DEBUG",
    [LogLevel.Info]: "INFO",
    [LogLevel.Warning]: "WARNING",
    [LogLevel.Error]: "ERROR",
};

function clampCameraSmoothing(value: string | number | undefined): number {
    if (value === undefined) return .5;
    const parsed = typeof value === "number" ? value : parseFloat(value);
    return Number.isFinite(parsed) ? Math.max(0, Math.min(1, parsed)) : .5;
}

function clampCameraSpeed(value: string | number | undefined): number {
    if (value === undefined) return 1;
    const parsed = typeof value === "number" ? value : parseFloat(value);
    return Number.isFinite(parsed) ? Math.max(0, Math.min(100, parsed)) : 1;
}
